import { useState, useEffect } from "react";
import { Flex, Text, Alert, AlertIcon, Box } from "@chakra-ui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Loading from "./Loading";

// One game's stats + chart + empty state, fetched independently by
// gameName so switching tabs only ever requests that tab's data (the
// history endpoint already supports a `gameName` filter server-side).
const GameHistoryPanel = ({ gameName, label }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get("/api/v1/game-history", {
          params: { gameName },
        });
        if (!cancelled) setStats(data);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) {
          // Session expired while on this page - send them back to log
          // in rather than showing a broken/empty chart.
          navigate("/");
          return;
        }
        setError("Unable to load your game history right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [gameName, navigate]);

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="50vh">
        <Loading />
      </Flex>
    );
  }

  const chartData = stats
    ? [{ name: label, Wins: stats.wins, Losses: stats.losses }]
    : [];

  return (
    <Flex direction="column" align="center" gap="24px" p={{ base: 4, md: 8 }}>
      {error && (
        <Alert status="error" maxW="500px">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {stats && stats.totalGames === 0 && (
        <Text color="gray.600">No games played yet.</Text>
      )}

      {stats && stats.totalGames > 0 && (
        <>
          <Flex gap={8} wrap="wrap" justify="center">
            <Box textAlign="center">
              <Text fontSize="sm" color="gray.500">
                Games Played
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="headline.400">
                {stats.totalGames}
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="sm" color="gray.500">
                Wins
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="green.500">
                {stats.wins}
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="sm" color="gray.500">
                Losses
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="red.500">
                {stats.losses}
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="sm" color="gray.500">
                Win Rate
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="headline.400">
                {stats.winRate}%
              </Text>
            </Box>
          </Flex>

          <Box w="100%" maxW="500px" h="320px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Wins" stackId="games" fill="#38A169" />
                <Bar dataKey="Losses" stackId="games" fill="#E53E3E" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Flex>
  );
};

export default GameHistoryPanel;
